import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { formatMessage, type MessageValues } from './format.ts';
import { ar } from './locales/ar.ts';
import { en, type Catalogue, type MessageKey } from './locales/en.ts';
import { hi } from './locales/hi.ts';
import { ta } from './locales/ta.ts';

export interface LocaleMeta {
  code: string;
  name: string;
  /** Endonym — a language picker that lists "Hindi" instead of "हिन्दी" is a
   *  picker for people who already read English. */
  native: string;
  dir: 'ltr' | 'rtl';
  status: 'complete' | 'in-review';
}

/**
 * Launch set is en, hi, ta, te, bn, mr; second wave ar (RTL), es, pt-BR, id
 * (§10). This build ships the ones that have real catalogues; the rest are
 * deliberately absent rather than present-and-English.
 */
export const LOCALES: LocaleMeta[] = [
  { code: 'en', name: 'English', native: 'English', dir: 'ltr', status: 'complete' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', dir: 'ltr', status: 'complete' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்', dir: 'ltr', status: 'in-review' },
  { code: 'ar', name: 'Arabic', native: 'العربية', dir: 'rtl', status: 'in-review' },
];

const CATALOGUES: Record<string, Catalogue> = { en, hi, ta, ar };

/**
 * Locale-dependent card indices (§10). J/Q/K are V/D/R in a French deck and
 * B/D/K in a German one — the index is a themed asset, not a hardcoded
 * character. Indian and diaspora decks use the English indices, which is why
 * `hi` and `ta` inherit them rather than transliterating.
 */
const CARD_INDEX_OVERRIDES: Record<string, Partial<Record<string, string>>> = {
  fr: { J: 'V', Q: 'D', K: 'R', A: 'A' },
  de: { J: 'B', Q: 'D', K: 'K', A: 'A' },
};

interface I18nValue {
  locale: string;
  dir: 'ltr' | 'rtl';
  setLocale: (code: string) => void;
  t: (key: MessageKey, values?: MessageValues) => string;
  cardIndex: (rank: string) => string;
  formatNumber: (value: number) => string;
  formatRelativeTime: (target: number) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function detectLocale(): string {
  const stored = localStorage.getItem('rummy.locale');
  if (stored && CATALOGUES[stored]) return stored;
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const base = candidate.split('-')[0]!;
    if (CATALOGUES[base]) return base;
  }
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => {
    try {
      return detectLocale();
    } catch {
      return 'en';
    }
  });

  const meta = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]!;

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = meta.dir;
    try {
      localStorage.setItem('rummy.locale', locale);
    } catch {
      /* private browsing */
    }
  }, [locale, meta.dir]);

  const setLocale = useCallback((code: string) => setLocaleState(code), []);

  const value = useMemo<I18nValue>(() => {
    const catalogue = CATALOGUES[locale] ?? en;
    const t = (key: MessageKey, values: MessageValues = {}) => {
      // Fall back to English for a key the translator has not reached yet.
      // Showing the key itself is a bug that reaches users.
      const message = catalogue[key] ?? en[key] ?? String(key);
      return formatMessage(message, values, locale);
    };
    return {
      locale,
      dir: meta.dir,
      setLocale,
      t,
      cardIndex: (rank: string) =>
        CARD_INDEX_OVERRIDES[locale]?.[rank] ?? (en as Record<string, string>)[`card.index.${rank}`] ?? rank,
      formatNumber: (n: number) => new Intl.NumberFormat(locale).format(n),
      formatRelativeTime: (target: number) => {
        const delta = Math.round((target - Date.now()) / 1000);
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const abs = Math.abs(delta);
        if (abs < 60) return rtf.format(delta, 'second');
        if (abs < 3600) return rtf.format(Math.round(delta / 60), 'minute');
        if (abs < 86400) return rtf.format(Math.round(delta / 3600), 'hour');
        return rtf.format(Math.round(delta / 86400), 'day');
      },
    };
  }, [locale, meta.dir, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n outside I18nProvider');
  return value;
}

export type { MessageKey };
